import React, { useState, useEffect, useRef, useMemo } from 'react'

import { LinkOutlined, PlusOutlined, QuestionCircleFilled, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useIsStandard } from '@contexts/operator/PlanContext'
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext'
import { useExport } from '@hooks/shared/useExport'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import ListPageHeader from '@shared/ListPageHeader'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { MultipleValue } from '@shared/table/Rules/component'

import { pickingListService } from '@services/operator/pickingListService'
import PickingConceptModal from '@shared/picking_lists/PickingConceptModal'
import { getColorForText } from '@/utils/colorTag';

interface SalesChannel {
  id: string;
  name: string;
}

interface PickingWaveRef {
  id: string;
  name: string;
}

interface PickingListRow {
  id: string;
  display_name?: string;
  picking_wave?: PickingWaveRef | null;
  merchant?: { name?: string } | null;
  picking_method?: string;
  purpose?: string;
  sales_channels?: SalesChannel[];
  fulfillments_count?: number;
  total_quantity?: number;
  status?: string;
  created_at?: string;
  [key: string]: unknown;
}

export default function OperatorPickingListList() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [lastModified, setLastModified] = useState(0)
  const [conceptModalVisible, setConceptModalVisible] = useState(false)
  const { merchants } = useOperatorData()
  const isStandard = useIsStandard()
  const { addJob, jobs } = useOperatorNotification()

  // 當有背景任務完成時，自動重新載入列表
  const prevCompletedCountRef = useRef(0)
  useEffect(() => {
    const completedCount = jobs.filter((j) => j.status === 'completed').length
    if (prevCompletedCountRef.current > 0 && completedCount > prevCompletedCountRef.current) {
      setLastModified(Date.now())
    }
    prevCompletedCountRef.current = completedCount
  }, [jobs])

  // useExport 與 pickingListService 仍是 .js，TS 對 hook options 與 service.export
  // 的簽名推導不完整（hook JSDoc 只列 addJob/jobName；service.export 推為 1 參），
  // 因此這裡 cast 成 any 維持原 runtime 行為。
  const exportService = pickingListService as any
  const handleExport = (useExport as any)({
    action: pickingListService ? (ids: string[]) => exportService.export(ids, { locale: i18n.language }) : undefined,
    endpoint: 'picking_lists/export',
    addJob,
    jobName: 'shared.picking_lists.list.export.job_name',
    i18nKeys: {
      noSelection: 'operator.picking_lists.list.export.no_selection',
      success: 'operator.picking_lists.list.export.success',
      error: 'operator.picking_lists.list.export.error',
    }
  })

  const handleDownloadAttachments = (useExport as any)({
    action: pickingListService ? (ids: string[]) => exportService.downloadAttachments(ids) : undefined,
    endpoint: 'picking_lists/download_attachments',
    addJob,
    jobName: 'operator.picking_lists.list.download_attachments.button',
    i18nKeys: {
      noSelection: 'operator.picking_lists.list.download_attachments.no_selection',
      success: 'operator.picking_lists.list.download_attachments.success',
      error: 'operator.picking_lists.list.download_attachments.error',
    }
  })

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: `shared.picking_lists.detail.wave.label`,
      persist: true,
      sortable: false,
      width: '120px',
      wrap: true,
      cell: ({ picking_wave }: PickingListRow) => picking_wave ? (
        <Link to={`/picking_waves/${picking_wave.id}`} style={{ wordBreak: 'break-word' }}>
          {picking_wave.name}
        </Link>
      ) : '-',
    },
    {
      nameKey: `models.picking_list.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'picking_lists.id',
      width: '120px',
      wrap: true,
      cell: ({ id, display_name }: PickingListRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/picking_lists/${id}`} style={{ wordBreak: 'break-word' }}>
            {display_name}
          </Link>
          <a
            href={`/operator/picking_lists/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: `models.merchant.name`,
      persist: true,
      sortable: false,
      width: '150px',
      wrap: true,
      selector: ({ merchant }: PickingListRow) => merchant?.name || '-',
    }]),
    {
      name: t('shared.picking_lists.generate.options.picking_method'),
      sortable: false,
      width: '120px',
      cell: ({ picking_method }: PickingListRow) => {
        const isApp = picking_method === 'app'
        return (
          <Tag color={isApp ? 'blue' : 'orange'}>
            {t(`shared.picking_lists.generate.options.${picking_method || 'app'}`)}
          </Tag>
        )
      },
    },
    {
      name: t('models.picking_list.purpose'),
      sortable: false,
      width: '120px',
      cell: ({ purpose }: PickingListRow) => {
        const isWorkOrder = purpose === 'work_order'
        return (
          <Tag color={isWorkOrder ? 'purple' : 'cyan'}>
            {isWorkOrder ? t('work_orders.picking_list.tag') : t('shared.picking_lists.purpose.fulfillment')}
          </Tag>
        )
      },
    },
    {
      nameKey: `models.sales_channel.name`,
      sortable: false,
      width: '200px',
      wrap: true,
      cell: ({ sales_channels }: PickingListRow) => {
        if (!sales_channels || sales_channels.length === 0) {
          return '-'
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {sales_channels.map((channel: SalesChannel) => (
              <Tag key={channel.id} color={getColorForText(channel.name)} style={{ margin: 0 }}>
                {channel.name}
              </Tag>
            ))}
          </div>
        )
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.picking_list.fulfillments_count')}
          <Tooltip title={t('operator.picking_lists.list.column_tooltips.fulfillments_count')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: ({ fulfillments_count }: PickingListRow) => fulfillments_count ?? 0,
    },
    {
      name: (
        <Space size={4}>
          {t('models.picking_list.total_quantity')}
          <Tooltip title={t('operator.picking_lists.list.column_tooltips.total_quantity')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: ({ total_quantity }: PickingListRow) => total_quantity ?? 0,
    },
    {
      name: (
        <Space size={4}>
          {t('models.picking_list.status')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('operator.picking_lists.list.column_tooltips.status')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '120px',
      cell: ({ status }: PickingListRow) => {
        let color = 'default'
        if (status === 'processing') color = 'orange'
        else if (status === 'done') color = 'green'
        else if (status === 'terminated') color = 'volcano'

        return (
          <Tag color={color}>
            {t(`enums.picking_list.status.${status}`)}
          </Tag>
        )
      },
    },
    {
      nameKey: `common.created_at`,
      sortable: true,
      sortFieldName: 'picking_lists.created_at',
      width: '180px',
      wrap: true,
      selector: ({ created_at }: PickingListRow) => created_at,
    },
  ], [t, isStandard])

  const supportRules = useMemo<SupportRule[]>(() => [
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t(`models.merchant.name`),
      component: MultipleValue,
      inputName: 'picking_lists.merchant_id',
      createable: false,
      defaultOptions: merchants.map((merchant: { id: string; name: string }) => ({
        label: merchant.name,
        value: merchant.id
      })),
      tooltip: {
        title: t('operator.picking_lists.list.filter_tooltips.merchant.title'),
        content: t('operator.picking_lists.list.filter_tooltips.merchant.content'),
      },
    }]),
    {
      label: t(`models.picking_list.status`),
      component: MultipleValue,
      inputName: 'picking_lists.status',
      createable: false,
      defaultOptions: [
        { label: t(`enums.picking_list.status.processing`), value: 'processing' },
        { label: t(`enums.picking_list.status.done`), value: 'done' },
        { label: t(`enums.picking_list.status.terminated`), value: 'terminated' },
      ],
      tooltip: {
        title: t('operator.picking_lists.list.filter_tooltips.status.title'),
        content: t('operator.picking_lists.list.filter_tooltips.status.content'),
      },
    },
    {
      label: t(`common.created_at`),
      component: FullDayRangePicker,
      inputName: 'picking_lists.created_at',
      tooltip: {
        title: t('operator.picking_lists.list.filter_tooltips.created_at.title'),
        content: t('operator.picking_lists.list.filter_tooltips.created_at.content'),
      },
    },
  ], [t, merchants, isStandard])

  // 操作按鈕定義
  const actions = useMemo<TableAction[]>(() => ([
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      do: handleExport
    } as unknown as TableAction,
    {
      name: `operator.picking_lists.list.download_attachments.button`,
      className: 'btn btn-primary btn-outline mb-0',
      do: handleDownloadAttachments
    } as unknown as TableAction
  ]), [handleExport, handleDownloadAttachments])

  return (
    <div>
      <ListPageHeader
        items={[
          { label: t('operator.menu.picking.title') },
          { label: t('operator.menu.picking_lists.list') },
        ]}
        guideButton={
          <Tooltip title={t('concepts.picking_lists.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/picking_lists/generate')}>
            {t('operator.menu.picking_lists.generate')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{ match: 'prefix', searchFields: [t('models.picking_list.name')] }}
        columnsEditable={true}
        tableId="operatorPickingListsTable"
        defaultQuery={{
          sorting_field: 'picking_lists.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        actions={actions}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={pickingListService as unknown as TableService}
      />
      <PickingConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  )
}
